// 1:1 port from age-of-mcp v3 (src/client/models/v3/road_bridge_head.ts) — the
// flattened part literals rebuilt from the original helpers + loops.
//
// v3 road_bridge_head — STONE PARAPET + ASPHALT, FLAT AT THE CONTRACT
// HEIGHT. The ramp onto a road bridge (docs/bridges.md): two battered stone
// courses carry an asphalt apron, and the lane marking continues across it
// as a yellow centre-dash PLUS (one dash toward each edge plus the centre
// square — symmetric under 90° turns, so the line reads as continuing in
// EITHER span direction; the model is never told the orientation), framed by
// four capped stone parapet pedestals at the corners. Symmetric across both
// axes. FOOTPRINT-NATIVE 2x2, centred on the origin.
// CRITICAL CONTRACT: the renderer stands actors on this head at a FIXED
// height — HEAD_TOP.road_bridge_head = 0.47 (renderer.ts) — so the asphalt
// apron's top sits at EXACTLY y = 0.47; the lane dashes bury their bodies in
// the asphalt with only a hair-thin sliver proud (the avenue's flush-marking
// pattern, so they never z-fight the deck).

import { defineModel, styleV3, type Part } from "../../lib";

const { ROOF_LOGI, STONE, STONE_DK, box, poles, slab } = styleV3;

const ASPHALT  = "#3b3b40";
const WALK_TOP = 0.47;        // the renderer's HEAD_TOP for this type — exact
const MARK_T   = 0.02;        // dash thickness — buried, top flush at WALK_TOP

// The battered stone abutment: a broad lower course and a narrower upper one.
function courses(upTo: number): Part[] {
  const all: Part[] = [
    box(1.92, 0.3, 1.92, STONE, [0, 0.15, 0]),
    box(1.8, 0.14, 1.8, STONE_DK, [0, 0.3 + 0.07, 0]),
  ];
  return all.slice(0, upTo);
}

// The asphalt apron — its top IS the walkable deck height.
function apron(): Part {
  return box(1.72, 0.03, 1.72, ASPHALT, [0, WALK_TOP - 0.015, 0]);
}

// The centre-line plus: a yellow dash toward each of the four edges and a
// centre square — bodies buried in the asphalt, tops a hair-thin sliver
// proud so they read without z-fighting the deck.
function laneMarks(): Part[] {
  const y = WALK_TOP - MARK_T / 2 + 0.001;   // a hair proud so it never z-fights
  return [
    box(0.1, MARK_T, 0.1, ROOF_LOGI, [0, y, 0]),
    box(0.1, MARK_T, 0.42, ROOF_LOGI, [0, y, 0.55]),
    box(0.1, MARK_T, 0.42, ROOF_LOGI, [0, y, -0.55]),
    box(0.42, MARK_T, 0.1, ROOF_LOGI, [0.55, y, 0]),
    box(0.42, MARK_T, 0.1, ROOF_LOGI, [-0.55, y, 0]),
  ];
}

// A capped parapet pedestal at each corner — identical on all four, so they
// frame the lanes whichever way the span runs.
function pedestals(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.81, 0.81]) {
    for (const z of [-0.81, 0.81]) {
      out.push(
        box(0.2, 0.2, 0.2, STONE_DK, [x, WALK_TOP + 0.1, z]),
        box(0.26, 0.05, 0.26, STONE, [x, WALK_TOP + 0.225, z]),
      );
    }
  }
  return out;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(2.0, 2.0, undefined, 0.1)];
  if (s === 1) return [slab(2.0, 2.0, undefined, 0.1), ...courses(1)];
  if (s === 2) return [slab(2.0, 2.0, undefined, 0.1), ...courses(2), ...poles(1.86, 1.86, 0.6)];
  return [
    slab(2.0, 2.0, undefined, 0.1),
    ...courses(2),
    apron(),
    ...laneMarks(),
    ...pedestals(),
  ];
}

export const roadBridgeHeadV3 = defineModel({
  id: "road-bridge-head-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Road bridge head (v3)",
  tags: ["building","road","bridge","head","low-poly","age-of-mcp","v3","water"],
  summary: "Low-poly road bridge head.",
  description: "A ramp onto a road bridge; a pair on opposite banks spans a gap with a two-lane deck for vehicles.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
